// Frame editor ("Rahmen bearbeiten" / new frame): title, sprint weeks, masterplan months, own
// columns, backlog column and swimlanes. A frame with own columns only is a table (strategy board,
// OKR, sprint contract …). Applied as one change (one undo step); the board itself is the preview.
import type { FrameLayout } from "./engine-api";
import { isoWeek, mondayOf } from "./iso";

const $ = <T extends HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector(sel) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};
const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/** First masterplan month after a sprint (same rule as FrameLayout::month_after). */
export function monthAfter(year: number, week: number, weeks: number): { year: number; month: number } {
  const d = new Date(mondayOf(year, week).getTime() + weeks * 7 * 864e5);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
}

export function defaultLayout(domains: string[]): FrameLayout {
  const now = isoWeek(new Date());
  return { title: "Sprint", start: now, weeks: 8, masterplan: null, months: 0, backlog: true, lanes: domains.map((title) => ({ id: null, title })), columns: [] };
}

/** A plain table: three own columns, three rows, no dates. */
export function tableLayout(): FrameLayout {
  const items = (word: string) => [1, 2, 3].map((i) => ({ id: null, title: `${word} ${i}` }));
  return { title: "Tabelle", start: null, weeks: 0, masterplan: null, months: 0, backlog: false, lanes: items("Zeile"), columns: items("Spalte") };
}

/** `capacity`: lanes only; `capTouched`: changed in this editor (else it is not sent, so a
 *  capacity someone else set meanwhile stays). NaN = not a number (refused on save). */
type Item = { id?: string | null; title: string; size?: number | null; capacity?: number | null; capTouched?: boolean; capText?: string };

/** "7.5" or "7,5" → 7.5; "" → null; anything else → NaN. */
export function parseCapacity(text: string): number | null {
  const t = text.trim().replace(",", ".");
  if (!t) return null;
  return /^\d+(\.\d+)?$/.test(t) ? Number(t) : NaN;
}
const capFmt = new Intl.NumberFormat("de-CH", { maximumFractionDigits: 1, useGrouping: false });

/** Offered sizes in cards (the engine rounds to halves and clamps, see layout.rs). */
const SIZES = {
  lane: [1, 1.5, 2, 3, 4, 5, 6, 8, 10, 12],
  column: [1, 2, 3, 4, 5, 6, 8, 10, 12, 16],
  caption: [1, 1.5, 2, 3, 4, 5, 6],
  week: [1, 2, 3, 4, 5, 6, 8, 10, 12],
  month: [2, 4, 6, 8, 10, 12, 16, 20, 24],
};
const DEFAULT_SIZE = { lane: 2, column: 4, caption: 2, week: 4, month: 8, backlog: 4 };
const num1 = new Intl.NumberFormat("de-CH", { maximumFractionDigits: 1 });

/** A size picker: "↕ 2" (height) or "↔ 4" (width), in cards; an unusual current value is kept. */
function sizeSelect(sizes: number[], value: number, sign: "↕" | "↔", label: string, id?: string): HTMLSelectElement {
  const sel = el("select", "field fe-size");
  if (id) sel.id = id;
  sel.title = label;
  sel.setAttribute("aria-label", label);
  const list = sizes.includes(value) ? sizes : [...sizes, value].sort((a, b) => a - b);
  for (const v of list) {
    const o = el("option", undefined, `${sign} ${num1.format(v)}`);
    o.value = String(v);
    o.selected = v === value;
    sel.appendChild(o);
  }
  return sel;
}

export class FrameEditor {
  private panel = $("#lw-frame-editor");
  private onSave: ((l: FrameLayout) => boolean) | null = null;
  private lanes: Item[] = [];
  private columns: Item[] = [];

  constructor(private release: () => void) {
    this.panel.addEventListener("keydown", (e) => {
      e.stopPropagation(); // board shortcuts stay off while typing here
      if (e.key === "Escape") this.close();
    });
  }

  get isOpen() { return !this.panel.hidden; }

  /**
   * Opens the editor; `save` applies the layout and returns false when it is refused. `templates`
   * (new frames only) are offered as a choice above the form.
   */
  open(heading: string, l: FrameLayout, action: string, save: (l: FrameLayout) => boolean, templates: { name: string; layout: FrameLayout }[] = []) {
    this.onSave = save;
    this.lanes = l.lanes.map((x) => ({ ...x, size: x.size ?? DEFAULT_SIZE.lane }));
    this.columns = (l.columns ?? []).map((x) => ({ ...x, size: x.size ?? DEFAULT_SIZE.column }));
    const p = this.panel;
    p.replaceChildren();
    const form = el("form", "fe-form");
    form.noValidate = true;
    form.appendChild(el("h2", "fe-title", heading));
    if (templates.length) {
      const row = el("div", "menu-row fe-templates");
      row.setAttribute("role", "group");
      row.setAttribute("aria-label", "Vorlage");
      for (const t of templates) {
        const b = el("button", "chip plain", t.name);
        b.type = "button";
        b.setAttribute("aria-pressed", String(t.layout.title === l.title));
        b.addEventListener("click", () => this.open(heading, t.layout, action, save, templates));
        row.appendChild(b);
      }
      form.appendChild(row);
    }
    const field = (label: string, input: HTMLElement, hint?: string) => {
      const w = el("label", "fe-field");
      w.appendChild(el("span", "menu-title", label));
      w.appendChild(input);
      if (hint) w.appendChild(el("span", "menu-hint", hint));
      return w;
    };
    const num = (id: string, v: number, min: number, max: number) => {
      const i = el("input", "field");
      i.id = id; i.type = "number"; i.min = String(min); i.max = String(max); i.value = String(v); i.inputMode = "numeric";
      return i;
    };
    /** "Breite je Woche   [↔ 4]" in one line. */
    const inline = (label: string, input: HTMLElement) => {
      const w = el("label", "fe-inline");
      w.append(el("span", "menu-hint", label), input);
      return w;
    };
    const check = (id: string, label: string, on: boolean) => {
      const w = el("label", "fe-check");
      const i = el("input");
      i.type = "checkbox"; i.id = id; i.checked = on;
      w.append(i, el("span", undefined, label));
      return w;
    };
    const title = el("input", "field");
    title.id = "fe-name"; title.maxLength = 120; title.value = l.title;
    form.appendChild(field("Titel", title));

    const now = isoWeek(new Date());
    const start = l.start ?? now;
    const sprint = el("fieldset", "fe-group");
    sprint.appendChild(check("fe-sprint", "Sprint – Kalenderwochen", l.weeks > 0 && l.start !== null));
    const sRow = el("div", "fe-row");
    sRow.append(field("Start-KW", num("fe-week", start.week, 1, 53)), field("Jahr", num("fe-year", start.year, 1900, 2200)), field("Wochen", num("fe-weeks", l.weeks || 8, 1, 104)));
    sprint.appendChild(sRow);
    sprint.appendChild(inline("Breite je Woche (in Karten)", sizeSelect(SIZES.week, l.weekSize ?? DEFAULT_SIZE.week, "↔", "Breite je Woche in Karten", "fe-week-size")));
    form.appendChild(sprint);

    const mp = el("fieldset", "fe-group");
    const mStart = l.masterplan ?? monthAfter(start.year, start.week, l.weeks || 8);
    mp.appendChild(check("fe-master", "Masterplan – Monate", l.months > 0 && l.masterplan !== null));
    const mRow = el("div", "fe-row");
    const mSel = el("select", "field");
    mSel.id = "fe-month";
    MONTHS.forEach((name, i) => { const o = el("option", undefined, name); o.value = String(i + 1); o.selected = i + 1 === mStart.month; mSel.appendChild(o); });
    mRow.append(field("Start-Monat", mSel), field("Jahr", num("fe-myear", mStart.year, 1900, 2200)), field("Monate", num("fe-months", l.months || 6, 1, 36)));
    mp.appendChild(mRow);
    mp.appendChild(inline("Breite je Monat (in Karten)", sizeSelect(SIZES.month, l.monthSize ?? DEFAULT_SIZE.month, "↔", "Breite je Monat in Karten", "fe-month-size")));
    mp.appendChild(el("span", "menu-hint", "In Monatsspalten zählt die Kalenderwoche unter der rechten oberen Kartenecke."));
    form.appendChild(mp);

    const listGroup = (kind: "lanes" | "columns", label: string, addLabel: string, hint?: string) => {
      const g = el("fieldset", "fe-group");
      g.appendChild(el("span", "menu-title", label));
      const list = el("ol", "fe-lanes");
      list.id = `fe-${kind}`;
      g.appendChild(list);
      const add = el("button", "menu-action", addLabel);
      add.type = "button";
      add.addEventListener("click", () => {
        this[kind].push({ id: null, title: "", size: kind === "lanes" ? DEFAULT_SIZE.lane : DEFAULT_SIZE.column });
        this.renderList(kind);
        (list.lastElementChild?.querySelector("input") as HTMLInputElement | null)?.focus();
      });
      g.appendChild(add);
      if (hint) g.appendChild(el("span", "menu-hint", hint));
      form.appendChild(g);
    };
    listGroup("columns", "Eigene Spalten (ohne Datum)", "+ Spalte", "Z. B. Ziel, Massnahme, Geparkt. Nur eigene Spalten = eine Tabelle.");
    const backlog = el("div", "fe-inline");
    backlog.append(check("fe-backlog", "Backlog-Spalte", l.backlog), sizeSelect(SIZES.week, l.backlogSize ?? DEFAULT_SIZE.backlog, "↔", "Breite der Backlog-Spalte in Karten", "fe-backlog-size"));
    form.appendChild(backlog);
    listGroup("lanes", "Spuren / Zeilen", "+ Spur", "↕ Höhe in Kartengrössen. SP: Kapazität der Spur in Story Points pro Woche – das Board zeigt dann in jeder Woche, wie viel davon verplant ist (leer = ohne).");
    const lanesGroup = $("#fe-lanes", form).parentElement!;
    lanesGroup.appendChild(inline("Breite der Spurnamen (in Karten)", sizeSelect(SIZES.caption, l.captionSize ?? DEFAULT_SIZE.caption, "↔", "Breite der Spalte mit den Spurnamen in Karten", "fe-caption-size")));

    form.appendChild(el("p", "menu-hint", "Karten in entfernten Wochen, Monaten, Spalten oder Spuren bleiben an ihrem Platz. Rückgängig macht die ganze Änderung rückgängig."));
    const err = el("p", "fe-error");
    err.id = "fe-error";
    err.setAttribute("role", "alert");
    form.appendChild(err);
    const actions = el("div", "fe-actions");
    const ok = el("button", "primary", action);
    ok.type = "submit";
    const cancel = el("button", undefined, "Abbrechen");
    cancel.type = "button";
    cancel.addEventListener("click", () => this.close());
    actions.append(ok, cancel);
    form.appendChild(actions);
    form.addEventListener("submit", (e) => { e.preventDefault(); this.submit(); });
    p.appendChild(form);
    this.renderList("lanes");
    this.renderList("columns");
    p.hidden = false;
    title.focus();
    title.select();
  }

  close() {
    if (this.panel.hidden) return;
    this.panel.hidden = true;
    this.onSave = null;
    this.release();
  }

  /** Lanes (rows) or own columns: rename, reorder, remove – ids stay with their entry. */
  private renderList(kind: "lanes" | "columns") {
    const items = this[kind];
    const lanes = kind === "lanes";
    const list = $(`#fe-${kind}`, this.panel);
    list.replaceChildren(...items.map((item, i) => {
      const li = el("li", "fe-lane");
      const input = el("input", "field");
      input.value = item.title;
      input.maxLength = 60;
      input.placeholder = lanes ? "z. B. Elektronik" : "z. B. Massnahme";
      input.setAttribute("aria-label", `${lanes ? "Spur" : "Spalte"} ${i + 1}`);
      input.addEventListener("input", () => { item.title = input.value; });
      const size = sizeSelect(lanes ? SIZES.lane : SIZES.column, item.size ?? (lanes ? DEFAULT_SIZE.lane : DEFAULT_SIZE.column), lanes ? "↕" : "↔", `${lanes ? "Höhe der Spur" : "Breite der Spalte"} ${i + 1} in Karten`);
      size.addEventListener("change", () => { item.size = Number(size.value); });
      // lanes: capacity in story points per week (X5); empty = none
      const cap = el("input", "field fe-cap");
      if (lanes) {
        cap.type = "text"; cap.inputMode = "decimal"; cap.maxLength = 7; cap.autocomplete = "off";
        cap.placeholder = "SP";
        cap.value = item.capText ?? (item.capacity != null ? capFmt.format(item.capacity) : "");
        const label = `Kapazität der Spur ${i + 1} in Story Points pro Woche`;
        cap.title = label;
        cap.setAttribute("aria-label", label);
        cap.addEventListener("input", () => { item.capText = cap.value; item.capacity = parseCapacity(cap.value); item.capTouched = true; });
      }
      const btn = (label: string, aria: string, fn: () => void, disabled = false) => {
        const b = el("button", "fe-icon", label);
        b.type = "button"; b.title = aria; b.setAttribute("aria-label", aria); b.disabled = disabled;
        b.addEventListener("click", () => { fn(); this.renderList(kind); });
        return b;
      };
      const swap = (j: number) => { [items[i], items[j]] = [items[j], items[i]]; };
      li.append(
        input,
        size,
        ...(lanes ? [cap] : []),
        btn(lanes ? "↑" : "←", lanes ? "Nach oben" : "Nach links", () => swap(i - 1), i === 0),
        btn(lanes ? "↓" : "→", lanes ? "Nach unten" : "Nach rechts", () => swap(i + 1), i === items.length - 1),
        btn("✕", lanes ? "Spur entfernen" : "Spalte entfernen", () => { items.splice(i, 1); }, lanes && items.length === 1),
      );
      return li;
    }));
  }

  /** Reads the form; null + message when something is out of range. */
  read(): FrameLayout | null {
    const p = this.panel;
    const val = (id: string) => Number(($(`#${id}`, p) as HTMLInputElement).value);
    const on = (id: string) => ($(`#${id}`, p) as HTMLInputElement).checked;
    const err = (m: string) => { $("#fe-error", p).textContent = m; return null; };
    const sprint = on("fe-sprint"), master = on("fe-master");
    const columns = this.columns.map((c) => ({ id: c.id ?? null, title: c.title.trim(), size: c.size ?? null }));
    if (!sprint && !master && !columns.length) return err("Bitte Sprint-Wochen, Masterplan-Monate oder eigene Spalten wählen.");
    const week = val("fe-week"), year = val("fe-year"), weeks = val("fe-weeks");
    const month = val("fe-month"), myear = val("fe-myear"), months = val("fe-months");
    if (sprint && !(Number.isInteger(week) && week >= 1 && week <= 53 && Number.isInteger(weeks) && weeks >= 1 && weeks <= 104 && year >= 1900 && year <= 2200)) return err("Sprint: KW 1–53, 1–104 Wochen.");
    if (master && !(Number.isInteger(months) && months >= 1 && months <= 36 && myear >= 1900 && myear <= 2200)) return err("Masterplan: 1–36 Monate.");
    if (this.lanes.some((l) => l.capacity != null && !(Number.isFinite(l.capacity) && l.capacity >= 0 && l.capacity <= 9999))) return err("Kapazität: eine Zahl von 0 bis 9999 Story Points pro Woche (z. B. 7.5).");
    // only changed capacities are sent (0 removes one); missing keeps the board's current value
    const lanes = this.lanes.map((l) => ({ id: l.id ?? null, title: l.title.trim(), size: l.size ?? null, ...(l.capTouched ? { capacity: l.capacity ?? 0 } : {}) }));
    if (!lanes.length) return err("Mindestens eine Spur.");
    return {
      title: ($("#fe-name", p) as HTMLInputElement).value.trim() || "Rahmen",
      start: sprint ? { year, week } : null,
      weeks: sprint ? weeks : 0,
      masterplan: master ? { year: myear, month } : null,
      months: master ? months : 0,
      backlog: on("fe-backlog"),
      lanes,
      columns,
      captionSize: val("fe-caption-size"),
      weekSize: val("fe-week-size"),
      monthSize: val("fe-month-size"),
      backlogSize: val("fe-backlog-size"),
    };
  }

  private submit() {
    const l = this.read();
    if (!l || !this.onSave) return;
    if (!this.onSave(l)) {
      $("#fe-error", this.panel).textContent = "Diese Einstellungen sind ungültig (z. B. KW 53 in einem Jahr mit 52 Wochen).";
      return;
    }
    this.close();
  }
}
